import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  Star,
  Sms,
  Group,
  Schedule,
  NotificationImportant,
  Timeline,
  Security
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';

const Pricing: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    { icon: <Sms />, text: 'Multiple daily wellness checks' },
    { icon: <Group />, text: 'Up to 4 caregivers in your care circle' },
    { icon: <Schedule />, text: 'Flexible scheduling and timezone support' },
    { icon: <NotificationImportant />, text: 'Instant missed check-in alerts' },
    { icon: <Timeline />, text: 'Comprehensive dashboard and reporting' },
    { icon: <Security />, text: 'Secure, easy to use platform' }
  ];

  return (
    <LandingLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Hero Section */}
        <Box textAlign="center" sx={{ mb: 8 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
            Simple, Affordable Pricing
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            Choose the plan that works best for you. All plans include the same great features.
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center" sx={{ mb: 8 }}>
          {/* Monthly Plan */}
          <Grid item xs={12} md={6} lg={5}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%', 
                position: 'relative',
                border: '2px solid #E8F5E8',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Monthly Plan
                </Typography>
                
                <Box sx={{ my: 4 }}>
                  <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                    $14<Typography component="span" variant="h4">.99</Typography>
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    per month
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Perfect for getting started with flexible monthly billing
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/signup')}
                  sx={{
                    backgroundColor: '#2E7D32',
                    '&:hover': {
                      backgroundColor: '#1B5E20'
                    },
                    py: 1.5,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  Get Started
                </Button>

                <Typography variant="body2" color="text.secondary">
                  Cancel anytime
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Annual Plan */}
          <Grid item xs={12} md={6} lg={5}>
            <Card 
              elevation={6}
              sx={{ 
                height: '100%', 
                position: 'relative',
                border: '3px solid #2E7D32',
                background: 'linear-gradient(135deg, #E8F5E8 0%, #F1F8E9 100%)',
                '&:hover': {
                  boxShadow: 8,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              {/* Popular Badge */}
              <Chip
                icon={<Star />}
                label="Most Popular"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#2E7D32',
                  color: 'white',
                  fontWeight: 'bold',
                  zIndex: 1
                }}
              />

              <CardContent sx={{ p: 4, pt: 6, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Annual Plan
                </Typography>
                
                <Box sx={{ my: 4 }}>
                  <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                    $130
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    per year
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 1 }}>
                    Save $49.88 annually!
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Best value with significant savings when you pay in advance
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/signup')}
                  sx={{
                    backgroundColor: '#1B5E20',
                    '&:hover': {
                      backgroundColor: '#0E3311'
                    },
                    py: 1.5,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  Get Started
                </Button>

                <Typography variant="body2" color="text.secondary">
                  Save $49.88 per year
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Features Section */}
        <Paper elevation={2} sx={{ p: 4, mb: 6, backgroundColor: '#F9F9F9' }}>
          <Typography variant="h4" gutterBottom textAlign="center" sx={{ color: '#2E7D32', mb: 4 }}>
            Everything You Need Included
          </Typography>
          
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    backgroundColor: '#E8F5E8', 
                    borderRadius: '50%', 
                    p: 1, 
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(feature.icon, { sx: { color: '#2E7D32' } })}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {feature.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom textAlign="center" sx={{ color: '#2E7D32', mb: 4 }}>
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Can I cancel anytime?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes! You can cancel your subscription at any time. There are no long-term contracts 
                  or cancellation fees.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Is my data secure?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes! We use industry-standard encryption and security measures to protect your 
                  personal information and ensure your privacy.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  What payment methods do you accept?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We accept all major credit cards (Visa, MasterCard, American Express) and 
                  process payments securely through Stripe.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  How many people can I monitor?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each subscription covers one loved one with up to 4 caregivers in their care circle. 
                  Contact us for multiple loved ones.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box textAlign="center" sx={{ py: 4, backgroundColor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#212121', fontWeight: 600 }}>
            Get Started Today
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: '#757575' }}>
            Join families who trust Casanté for daily peace of mind
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              backgroundColor: '#2E7D32',
              '&:hover': {
                backgroundColor: '#1B5E20'
              },
              px: 6,
              py: 2,
              fontSize: '1.2rem'
            }}
          >
            Get Started Now
          </Button>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default Pricing;
